import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MaldivesImg from '../assets/images/Maldives.jpg'
import { Icon, Stars } from '../components/Icons'
import { usePackages } from '../hooks/usePackages'
import { findMatchingPackage } from '../utils/packageUtils'

export default function PackageDetails() {
  const { packageName } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('itinerary')
  const [openDay, setOpenDay] = useState(1)
  const [selectedImg, setSelectedImg] = useState(null)

  // WhatsApp Helper function
  const sendToWhatsApp = (message) => {
    const phone = '7092210922'
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }

  // State hooks for sidebar form
  const [sidebarName, setSidebarName] = useState('')
  const [sidebarEmail, setSidebarEmail] = useState('')
  const [sidebarPhone, setSidebarPhone] = useState('')

  // Scroll to top on load and reset selected image on package change
  useEffect(() => {
    window.scrollTo(0, 0)
    setSelectedImg(null)
  }, [packageName])

  const { packages, loading } = usePackages()

  const matchedP = findMatchingPackage(packages, packageName)

  const rawP = matchedP || {
    name: packageName || 'Explore the World',
    location: 'Custom Destination',
    duration: '5 Nights / 6 Days',
    price: '₹ 25,000',
    img: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?auto=format&fit=crop&q=80&w=1200&h=800',
  }

  const p = {
    ...rawP,
    duration:
      rawP.duration ||
      (rawP.nights ? `${Number(rawP.nights) + 1} Days / ${rawP.nights} Nights` : '5 Nights'),
    location: rawP.location || rawP.city || rawP.country || rawP.region || rawP.name,
    price: rawP.from || (rawP.price ? (typeof rawP.price === 'number' ? `₹ ${rawP.price.toLocaleString('en-IN')}` : rawP.price) : '₹ 25,000'),
    img: rawP.image || rawP.img || MaldivesImg,
  }

  const handleSidebarSubmit = (e) => {
    e?.preventDefault()
    if (!sidebarName || !sidebarPhone || !sidebarEmail) {
      alert('Please fill out all required fields.')
      return
    }
    const message = `Hello DIRA HOLIDAYS! I would like to request a quote for a tour.

*Request Quote Details:*
- *Tour:* ${p.name}
- *Name:* ${sidebarName}
- *Email:* ${sidebarEmail}
- *Phone:* ${sidebarPhone}`
    sendToWhatsApp(message)
  }

  const itinerary = (p.itinerary && p.itinerary.length > 0) ? p.itinerary : [
    {
      day: 1,
      title: `Welcome to ${p.name}`,
      content: `Arrival at airport/station. Transfer to your premium hotel in ${p.name}. Evening at leisure.`,
    },
    {
      day: 2,
      title: `${p.name} Local Exploration`,
      content: `Full day sightseeing of major landmarks and cultural sites in ${p.name}.`,
    },
    {
      day: 3,
      title: 'Nature & Adventure',
      content: 'Explore the scenic beauty and hidden gems of the region with guided assistance.',
    },
    {
      day: 4,
      title: 'Cultural Immersion',
      content: 'Experience local traditions, cuisine, and artisanal crafts of the area.',
    },
    {
      day: 5,
      title: 'Shopping & Free Time',
      content: 'Day at leisure for personal exploration or souvenir shopping in local markets.',
    },
    {
      day: 6,
      title: 'Departure',
      content: `Check-out from hotel. Final transfer to the departure point with sweet memories of ${p.name}.`,
    },
  ]

  if (loading) {
    return (
      <div
        className="package-details-loading"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '120px 20px',
        }}
      >
        <div
          style={{
            width: '44px',
            height: '44px',
            border: '4px solid #e2e8f0',
            borderTopColor: 'var(--brand)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--stone)', fontSize: '15px', fontWeight: 600 }}>
          Loading package details...
        </p>
      </div>
    )
  }

  return (
    <div className="package-details page-enter">
      {/* HERO SECTION */}
      <section
        className="booking-hero"
        style={{
          position: 'relative',
          padding: '110px 0 90px',
          overflow: 'hidden',
          background: '#0d1117',
        }}
      >
        {/* Background Image: Package first image as banner */}
        {p.img && (
          <img
            src={p.img}
            alt={p.name}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: 0,
            }}
          />
        )}

        {/* Elegant dark gradient overlay for text readability */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(13,17,23,0.65) 0%, rgba(13,17,23,0.85) 100%)',
            zIndex: 1,
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="hero-content">
            <span
              className="eyebrow"
              style={{
                color: 'var(--secondary)',
                letterSpacing: '3px',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '12px',
                textShadow: '0 2px 8px rgba(0,0,0,0.6)',
              }}
            >
              DIRA HOLIDAYS Signature Journey
            </span>
            <h1
              className="product-title"
              style={{
                color: 'var(--white)',
                fontSize: 'clamp(32px, 5vw, 56px)',
                fontWeight: 900,
                marginBottom: '20px',
                textShadow: '0 2px 14px rgba(0,0,0,0.7)',
              }}
            >
              {p.name}
            </h1>
            <div className="rating-row" style={{ color: 'var(--white)' }}>
              <Stars rating={4.8} />
              <span className="review-count" style={{ opacity: 0.9, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                (156 Reviews)
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK INFO BAR */}
      <div className="info-bar-wrapper">
        <div className="container info-bar">
          <div className="info-item">
            <Icon name="clock" size={20} color="var(--secondary)" />
            <div className="meta">
              <label>Duration</label>
              <span>{p.duration}</span>
            </div>
          </div>
          <div className="info-item">
            <Icon name="tag" size={20} color="var(--secondary)" />
            <div className="meta">
              <label>Best Price</label>
              <span>{p.price}</span>
            </div>
          </div>
          <div className="info-item">
            <Icon name="users" size={20} color="var(--secondary)" />
            <div className="meta">
              <label>Type</label>
              <span>Fixed Group Tour</span>
            </div>
          </div>
          <div className="info-item">
            <Icon name="globe" size={20} color="var(--secondary)" />
            <div className="meta">
              <label>Location</label>
              <span>{p.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container main-grid">
        {/* LEFT: CONTENT */}
        <div className="product-main">
          {/* MEDIA GALLERY */}
          <div className="media-gallery">
            <div className="main-img-wrap" style={{ cursor: 'pointer' }}>
              <img src={selectedImg || p.img} alt={p.name} />
            </div>
            {p.galleryImages && p.galleryImages.length > 0 && (
              <div className="thumb-row" style={{ marginTop: '10px' }}>
                <div
                  className="thumb-wrap"
                  onClick={() => setSelectedImg(p.img)}
                  style={{
                    border:
                      selectedImg === p.img || !selectedImg
                        ? '2px solid var(--brand)'
                        : '2px solid transparent',
                  }}
                >
                  <img src={p.img} alt="cover" />
                </div>
                {p.galleryImages.slice(0, 4).map((gImg, idx) => (
                  <div
                    key={idx}
                    className="thumb-wrap"
                    onClick={() => setSelectedImg(gImg)}
                    style={{
                      border:
                        selectedImg === gImg ? '2px solid var(--brand)' : '2px solid transparent',
                    }}
                  >
                    <img src={gImg} alt={`gallery-${idx}`} />
                  </div>
                ))}
                {p.galleryImages.length > 4 && (
                  <div
                    className="thumb-wrap"
                    onClick={() => setSelectedImg(p.galleryImages[4])}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#000',
                      color: 'var(--white)',
                      fontSize: '14px',
                      fontWeight: '800',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border:
                        selectedImg === p.galleryImages[4]
                          ? '2px solid var(--brand)'
                          : '2px solid transparent',
                    }}
                  >
                    <img
                      src={p.galleryImages[4]}
                      alt="more-photos"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.3,
                      }}
                    />
                    <span style={{ position: 'relative', zIndex: 2 }}>
                      +{p.galleryImages.length - 4} More
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* TABS */}
          <div className="tabs-container">
            <div className="tabs-header">
              <button
                className={activeTab === 'itinerary' ? 'active' : ''}
                onClick={() => setActiveTab('itinerary')}
              >
                Itinerary
              </button>
              <button
                className={activeTab === 'inclusions' ? 'active' : ''}
                onClick={() => setActiveTab('inclusions')}
              >
                Inclusions
              </button>
              <button
                className={activeTab === 'info' ? 'active' : ''}
                onClick={() => setActiveTab('info')}
              >
                Policies
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'itinerary' && (
                <div className="itinerary-list">
                  {itinerary.map((item) => (
                    <div
                      key={item.day || item._id}
                      className={`details-accordion ${openDay === item.day ? 'active' : ''}`}
                    >
                      <button
                        className="accordion-trigger"
                        onClick={() => setOpenDay(openDay === item.day ? null : item.day)}
                      >
                        <div className="trigger-content">
                          <span className="day-label">Day {item.day}</span>
                          <span className="day-title">{item.title}</span>
                        </div>
                        <span className="chevron">▶</span>
                      </button>
                      <div
                        className="accordion-panel"
                        style={{
                          maxHeight: openDay === item.day ? '1000px' : '0',
                          padding: openDay === item.day ? '20px' : '0',
                        }}
                      >
                        <p style={{ fontSize: '13px', lineHeight: '1.7', color: 'var(--stone)', whiteSpace: 'pre-line' }}>
                          {item.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {activeTab === 'inclusions' && (
                <div
                  className="inclusions-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '30px',
                    padding: '30px',
                  }}
                >
                  <div className="inc-col">
                    <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Inclusions</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {p.inclusions && p.inclusions.length > 0 ? (
                        p.inclusions.map((inc, i) => (
                          <li
                            key={i}
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                            }}
                          >
                            <Icon name="check" size={16} color="var(--brand-deep)" /> {inc}
                          </li>
                        ))
                      ) : (
                        <>
                          <li
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                            }}
                          >
                            <Icon name="check" size={16} color="var(--brand-deep)" /> Professional
                            Guide in {p.name.split(' ')[0]}
                          </li>
                          <li
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                            }}
                          >
                            <Icon name="check" size={16} color="var(--brand-deep)" /> Breakfast &
                            Dinner (Local Cuisine)
                          </li>
                          <li
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                            }}
                          >
                            <Icon name="check" size={16} color="var(--brand-deep)" /> All transfers
                            in AC Coach/Car
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="inc-col">
                    <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Exclusions</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {p.exclusions && p.exclusions.length > 0 ? (
                        p.exclusions.map((exc, i) => (
                          <li
                            key={i}
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                              opacity: 0.6,
                            }}
                          >
                            <Icon name="check" size={16} color="var(--muted)" /> {exc}
                          </li>
                        ))
                      ) : (
                        <>
                          <li
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                              opacity: 0.6,
                            }}
                          >
                            <Icon name="check" size={16} color="var(--muted)" /> Personal shopping
                            and tips
                          </li>
                          <li
                            style={{
                              display: 'flex',
                              gap: '10px',
                              marginBottom: '10px',
                              fontSize: '13px',
                              opacity: 0.6,
                            }}
                          >
                            <Icon name="check" size={16} color="var(--muted)" /> Travel Insurance &
                            GST
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {activeTab === 'info' && (
                <div style={{ padding: '30px' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Policies & Guidelines</h3>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {p.policies && p.policies.length > 0 ? (
                      p.policies.map((pol, i) => (
                        <li
                          key={i}
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '10px',
                            fontSize: '13px',
                            lineHeight: '1.6',
                          }}
                        >
                          <Icon name="check" size={16} color="var(--brand-deep)" /> {pol}
                        </li>
                      ))
                    ) : (
                      <>
                        <li
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '10px',
                            fontSize: '13px',
                          }}
                        >
                          <Icon name="check" size={16} color="var(--brand-deep)" /> Standard
                          check-in / check-out times apply.
                        </li>
                        <li
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '10px',
                            fontSize: '13px',
                          }}
                        >
                          <Icon name="check" size={16} color="var(--brand-deep)" /> Valid ID proof
                          is required for all guests.
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: SIDEBAR */}
        <aside className="sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-title">Book This Tour</h3>
            <form className="form-grid" onSubmit={handleSidebarSubmit}>
              <input
                type="text"
                placeholder="Your Name"
                required
                value={sidebarName}
                onChange={(e) => setSidebarName(e.target.value)}
              />
              <input
                type="email"
                placeholder="Your Email"
                required
                value={sidebarEmail}
                onChange={(e) => setSidebarEmail(e.target.value)}
              />
              <div className="phone-group">
                <select style={{ width: '80px' }}>
                  <option>+91</option>
                </select>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  required
                  value={sidebarPhone}
                  onChange={(e) => setSidebarPhone(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="submit-btn"
                style={{ width: '100%', marginTop: '10px' }}
              >
                REQUEST A QUOTE
              </button>
            </form>

            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <button
                className="book-now-btn"
                style={{ width: '100%' }}
                onClick={() => navigate(`/booking/${packageName}`)}
              >
                PROCEED TO BOOK
              </button>
            </div>
          </div>

          <div className="sidebar-card">
            <h3 className="sidebar-title">Need Help?</h3>
            <a href="tel:+917092210922" className="contact-link">
              <Icon name="phone" size={18} />
              <span>+91 70922 10922</span>
            </a>
            <div className="contact-link">
              <Icon name="support" size={18} />
              <span>24/7 Expert Support</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
